import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, from } from 'rxjs';

import { authService as viveAuthService } from '../shared/vive-api/auth.service';
import {
  SessionDto,
  SessionImpersonationDto
} from '../shared/vive-api/vive-api-models';
import { AuthService } from './auth.service';
import { NavigationMenuService } from './navigation-menu.service';
import { ToastService } from './toast.service';
import { messageOf, statusOf } from './vive-api-errors';

/** Exactly one of these: a whole client account, or one client user. */
export type ImpersonationTarget = { companyId: number } | { userId: number };

// Where a staff sign-in lands (LoginComponent.routeByUser).
const STAFF_LANDING: (string | { viveStatus: string })[] = [
  'admin',
  'run-vive',
  { viveStatus: '' }
];
// The interceptor's own wording, which ManuallyHandleError switches off.
const GENERIC_ERROR_MESSAGE = 'An Error has occurred.';
const NETWORK_ERROR_MESSAGE =
  'Something went wrong on our end. Try refreshing the page.';
const ALREADY_IMPERSONATING_MESSAGE =
  "You're already viewing VIVE as another account. Return to your account first.";
const RETURN_FAILED_MESSAGE =
  "We couldn't return you to your account. Please try again.";

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private startInFlight = false;
  private returnInFlight = false;
  // Where to go if the Return in flight fails. Kept across calls, so a
  // `/logout` that arrives while the banner's Return is out is not stranded on
  // the logout page.
  private returnUrl?: string;

  constructor(
    private router: Router,
    private authService: AuthService,
    private navigationMenuService: NavigationMenuService,
    private toastService: ToastService
  ) {}

  public getImpersonation(): SessionImpersonationDto | null {
    return this.authService.getSession()?.session.impersonation ?? null;
  }

  public isImpersonating(): boolean {
    return this.authService.isImpersonating();
  }

  public start(target: ImpersonationTarget): void {
    if (this.startInFlight) {
      return;
    }
    this.startInFlight = true;

    from(
      viveAuthService.startImpersonation({
        data: target,
        options: { context: { ManuallyHandleError: true } }
      })
    )
      .pipe(
        finalize(() => {
          this.startInFlight = false;
        })
      )
      .subscribe({
        next: (session) => this.enterImpersonation(session),
        error: (error: unknown) => this.handleStartRefused(error)
      });
  }

  /**
   * `returnUrl` is where to go if the Return fails and the impersonation stays:
   * the page before `/logout`. Callers already on a page pass nothing and stay.
   */
  public returnToMyAccount(returnUrl?: string): void {
    this.returnUrl = returnUrl ?? this.returnUrl;
    if (this.returnInFlight) {
      return;
    }
    this.returnInFlight = true;

    from(
      viveAuthService.endImpersonation({
        options: { context: { ManuallyHandleError: true } }
      })
    )
      .pipe(
        finalize(() => {
          this.returnInFlight = false;
          this.returnUrl = undefined;
        })
      )
      .subscribe({
        next: (session) => {
          this.applySession(session);
          this.router.navigate(STAFF_LANDING);
        },
        error: (error: unknown) => this.handleReturnFailed(error)
      });
  }

  private enterImpersonation(session: SessionDto): void {
    this.applySession(session);
    this.router.navigate([
      'client',
      this.authService.getUserCompanyId(),
      'dashboard'
    ]);
  }

  private applySession(session: SessionDto): void {
    this.authService.seedSession(session);
    this.navigationMenuService.sendEvent('reconstructNavMenu');
  }

  private handleStartRefused(error: unknown): void {
    // While impersonating, the session principal is the client, so the API
    // refuses a second start: 403 at the role gate, or 409.
    // Otherwise the interceptor's buildErrorToastBody, plus its network-failure
    // branch.
    const status = statusOf(error);
    const message =
      status === 403 || status === 409
        ? ALREADY_IMPERSONATING_MESSAGE
        : [0, 502, 504].includes(status)
          ? NETWORK_ERROR_MESSAGE
          : (messageOf(error) ?? GENERIC_ERROR_MESSAGE);
    this.toastService.addErrorToast('Error', message);
  }

  private handleReturnFailed(error: unknown): void {
    // A 401 opens the sign-in modal before this runs. Signing in there seeded
    // a new session, so there is nothing left to return from, whatever the
    // retried call answered. The login page routes whoever signed in.
    if (!this.isImpersonating()) {
      this.navigationMenuService.sendEvent('reconstructNavMenu');
      this.router.navigate(['login']);
      return;
    }
    // The API no longer sees this tab's impersonation. 401: the staff session
    // ended (cookie cleared), or another tab's Return won the race (cookie
    // already staff). 409: another tab already returned. Either way the
    // browser holds a session this tab does not show.
    const status = statusOf(error);
    if (status === 401 || status === 409) {
      this.authService.restartOnBrowserSession();
      return;
    }
    // Anything else leaves the impersonation in place on the server.
    this.toastService.addErrorToast('Error', RETURN_FAILED_MESSAGE);
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    }
  }
}
